import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackButton, Badge, Button, Card, CardContent, CardHeader, CardTitle, OtpInput, PageHeader, SearchSelect } from '@doccareplus/ui';
import { BellRing, MonitorSmartphone, ShieldCheck } from 'lucide-react';
import { useTheme, useTranslation } from '@doccareplus/shared/lib';
import { authApi } from '@doccareplus/kernel/infrastructure/api/auth.service';
import { useAccountSettingsQuery, useSessionsQuery } from '../application/settings.query';
import { useRevokeSessionCommand, useSetTwoFactorCommand, useUpdateAccountSettingsCommand } from '../application/settings.command';
import type { AccountSettings } from '../infrastructure/settings.api';
import { useIsMobileSurface } from './shared/useIsMobileSurface';
import { AccountSettingsPageSkeleton, SessionsListSkeleton } from './SettingsSkeletons';

export function AccountSettingsPage() {
  const { t } = useTranslation('settings');
  const { setTheme } = useTheme();
  const navigate = useNavigate();
  const isMobile = useIsMobileSurface();
  const settings = useAccountSettingsQuery();
  const sessions = useSessionsQuery();
  const update = useUpdateAccountSettingsCommand();
  const revoke = useRevokeSessionCommand();
  const twoFactor = useSetTwoFactorCommand();
  const [draft, setForm] = useState<AccountSettings | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);

  const form = draft ?? settings.data;

  if (settings.isLoading) {
    return <AccountSettingsPageSkeleton label={t('account.title')} />;
  }

  if (settings.isError || !form) {
    return (
      <Card>
        <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 px-4 text-center">
          <p className="text-[13.5px] text-[var(--text-2)]">{t('common:errors.generic')}</p>
          <Button type="button" variant="ghost" onClick={() => void settings.refetch()}>
            {t('common:actions.retry')}
          </Button>
        </div>
      </Card>
    );
  }

  const save = async () => {
    await update.mutateAsync({
      theme: form.theme,
      language: 'en',
      notificationSoundsEnabled: form.notificationSoundsEnabled,
      inactivityTimeoutMinutes: form.inactivityTimeoutMinutes,
    });
    setTheme(form.theme.toLowerCase() as 'system' | 'light' | 'dark');
  };

  return <div className="space-y-4">
    {isMobile && <BackButton label={t('common:actions.back')} onClick={() => navigate('/mobile/more')} />}
    <PageHeader title={t('account.title')} subtitle={t('account.subtitle')} />
    <div className="grid gap-4 xl:grid-cols-2">
      <Card><CardHeader bordered><CardTitle>{t('account.preferences')}</CardTitle></CardHeader><CardContent className="space-y-4">
        <SearchSelect
          label={t('account.theme')}
          searchable={false}
          value={form.theme}
          onChange={(value) => setForm({ ...form, theme: value as AccountSettings['theme'] })}
          options={[
            { value: 'System', label: t('account.themeSystem') },
            { value: 'Light', label: t('account.themeLight') },
            { value: 'Dark', label: t('account.themeDark') },
          ]}
        />
        <SearchSelect label={t('account.language')} searchable={false} disabled value="en" onChange={() => {}} options={[{ value: 'en', label: 'English' }]} />
        <PreferenceToggle icon={<BellRing />} label={t('account.sounds')} checked={form.notificationSoundsEnabled} onChange={(checked) => setForm({ ...form, notificationSoundsEnabled: checked })} />
        <SearchSelect
          label={t('account.timeout')}
          searchable={false}
          value={String(form.inactivityTimeoutMinutes)}
          onChange={(value) => setForm({ ...form, inactivityTimeoutMinutes: Number(value) as AccountSettings['inactivityTimeoutMinutes'] })}
          options={[15, 30, 60, 120].map((minutes) => ({ value: String(minutes), label: t('account.minutes', { count: minutes }) }))}
        />
        <Button variant="primary" isLoading={update.isPending} onClick={() => void save()}>{t('actions.save')}</Button>
      </CardContent></Card>

      <Card><CardHeader bordered><CardTitle>{t('account.security')}</CardTitle></CardHeader><CardContent className="space-y-4">
        <div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 text-[var(--primary)]" /><div className="flex-1"><div className="font-semibold">{t('account.twoFactor')}</div><p className="text-[12px] text-[var(--text-3)]">{t('account.twoFactorHint')}</p></div><Badge variant={form.twoFactorEnabled ? 'success' : 'warning'}>{form.twoFactorEnabled ? t('status.enabled') : t('status.disabled')}</Badge></div>
        {!form.twoFactorEnabled && <><Button variant="ghost-sm" isLoading={sendingOtp} disabled={!form.phoneNumber} onClick={() => { if (!form.phoneNumber) return; setSendingOtp(true); void authApi.sendOtp({ channel: 'WhatsApp', recipient: form.phoneNumber, purpose: 'Login' }).finally(() => setSendingOtp(false)); }}>{t('account.sendOtp')}</Button><OtpInput label={t('account.otpCode')} value={otpCode} onChange={setOtpCode} hint={form.phoneNumber ? t('account.otpHint') : t('account.phoneRequired')} disabled={!form.phoneNumber} /></>}
        <Button variant="ghost" isLoading={twoFactor.isPending} disabled={!form.twoFactorEnabled && !otpCode.trim()} onClick={() => void twoFactor.mutateAsync({ enabled: !form.twoFactorEnabled, otpCode: form.twoFactorEnabled ? undefined : otpCode }).then(() => { setForm({ ...form, twoFactorEnabled: !form.twoFactorEnabled }); setOtpCode(''); })}>{form.twoFactorEnabled ? t('account.disableTwoFactor') : t('account.enableTwoFactor')}</Button>
      </CardContent></Card>
    </div>

    <Card><CardHeader bordered><CardTitle>{t('account.sessions')}</CardTitle></CardHeader><CardContent className="space-y-1">
      {sessions.isLoading ? (
        <SessionsListSkeleton label={t('account.sessions')} />
      ) : sessions.isError ? (
        <div className="flex min-h-24 flex-col items-center justify-center gap-3 text-center">
          <p className="text-[13px] text-[var(--text-2)]">{t('common:errors.generic')}</p>
          <Button type="button" variant="ghost-sm" onClick={() => void sessions.refetch()}>{t('common:actions.retry')}</Button>
        </div>
      ) : sessions.data?.length ? sessions.data.map((session) => <div key={session.id} className="flex flex-wrap items-center gap-3 border-b border-[var(--border)] py-3 last:border-0"><MonitorSmartphone className="h-5 w-5 text-[var(--text-3)]" /><div className="min-w-[220px] flex-1"><div className="font-semibold">{session.deviceName}</div><div className="text-[12px] text-[var(--text-3)]">{session.approximateLocation ?? t('account.unknownLocation')} · {t('account.lastActive')} {new Date(session.lastActivityAt).toLocaleString()}</div></div><Button variant="ghost-sm" isLoading={revoke.isPending} onClick={() => void revoke.mutateAsync(session.id)}>{t('account.revoke')}</Button></div>) : <p className="text-[13px] text-[var(--text-3)]">{t('account.noSessions')}</p>}
    </CardContent></Card>
  </div>;
}

function PreferenceToggle({ icon, label, checked, onChange }: { icon: ReactNode; label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return <label className="flex items-center gap-3 rounded-[10px] border border-[var(--border)] p-3"><span className="text-[var(--text-3)] [&>svg]:h-5 [&>svg]:w-5">{icon}</span><span className="flex-1 font-semibold">{label}</span><input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="h-4 w-4 accent-[var(--primary)]" /></label>;
}
